import { useState, useCallback, lazy, Suspense } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Search } from "lucide-react";
// Search UI downloads only when a reader opens it.
const SearchOverlay = lazy(() => import("./SearchOverlay"));

const Navigation = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { href: "/category/people", label: "People" },
    { href: "/category/companies", label: "Companies" },
    { href: "/category/brands", label: "Brands" },
    { href: "/category/initiatives", label: "Initiatives" },
    { href: "/category/specials", label: "Specials" },
    { href: "/about", label: "About" },
    { href: "#newsletter", label: "Subscribe", isAnchor: true },
    { href: "/flagship-ips", label: "Flagship IPs", highlight: true },
    { href: "/submit-story", label: "Submit a Story", cta: true },
  ];

  const isActive = (href) => location.pathname === href;
  const closeSearch = useCallback(() => setSearchOpen(false), []);

  return (
    <>
      <header
        className="sticky top-0 z-50 w-full bg-white/80 backdrop-blur-xl"
        style={{ borderBottom: '1px solid rgba(10,10,10,0.05)' }}
        data-testid="main-navigation"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            <Link to="/" className="flex-shrink-0" data-testid="nav-logo">
              <img src="/static/img/logo-black.svg" alt="People Who Matter" width="5516" height="1107" className="h-[33px] md:h-10 lg:h-[55px] w-auto" />
            </Link>

            <nav className="hidden lg:flex items-center gap-5 xl:gap-7 ml-6" data-testid="desktop-nav">
              {navLinks.map((link) => (
                link.isAnchor ? (
                  <a
                    key={link.href}
                    href={link.href}
                    onClick={(e) => {
                      e.preventDefault();
                      const el = document.getElementById("newsletter");
                      if (el) {
                        el.scrollIntoView({ behavior: "smooth" });
                      } else {
                        window.location.href = "/#newsletter";
                      }
                    }}
                    className="nav-link cursor-pointer"
                    data-testid={`nav-${link.label.toLowerCase()}`}
                  >
                    {link.label}
                  </a>
                ) : (
                  <Link
                    key={link.href}
                    to={link.href}
                    className={
                      link.highlight
                        ? `nav-link-highlight whitespace-nowrap ${isActive(link.href) ? "nav-link-highlight-active" : ""}`
                        : link.cta
                          ? `whitespace-nowrap text-[11px] font-bold tracking-[0.16em] uppercase transition-colors ${isActive(link.href) ? "text-neutral-950" : "text-neutral-500 hover:text-neutral-950"}`
                          : `nav-link whitespace-nowrap ${isActive(link.href) ? "nav-link-active" : ""}`
                    }
                    data-testid={`nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    {link.label}
                  </Link>
                )
              ))}
            </nav>

            <div className="flex items-center gap-3">
              <button
                className="p-2 hover:bg-black/5 transition-colors"
                onClick={() => setSearchOpen(true)}
                data-testid="search-button"
                aria-label="Search"
              >
                <Search className="w-4 h-4 text-neutral-500" />
              </button>

              <button
                className="lg:hidden p-2 hover:bg-black/5 transition-colors"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-button"
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-[100]" data-testid="mobile-menu">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setMobileMenuOpen(false)} />
          <div className="absolute top-16 left-0 right-0 bottom-0 bg-white overflow-y-auto">
            <nav className="px-6 py-8">
              {navLinks.map((link) => (
                link.isAnchor ? (
                  <a key={link.href} href={link.href} className="block font-serif text-xl py-4 text-neutral-950 hover:text-wine-700 transition-colors" style={{ borderBottom: '1px solid rgba(10,10,10,0.06)' }} onClick={(e) => { e.preventDefault(); setMobileMenuOpen(false); const el = document.getElementById("newsletter"); if (el) el.scrollIntoView({ behavior: "smooth" }); else window.location.href = "/home#newsletter"; }} data-testid={`mobile-nav-${link.label.toLowerCase()}`}>
                    {link.label}
                  </a>
                ) : (
                  <Link key={link.href} to={link.href} className={`block font-serif text-xl py-4 transition-colors ${link.highlight ? 'text-[#B89947] font-bold' : 'text-neutral-950 hover:text-wine-700'}`} style={{ borderBottom: '1px solid rgba(10,10,10,0.06)' }} onClick={() => setMobileMenuOpen(false)} data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}>
                  {link.label}
                </Link>
                )
              ))}
              <Link to="/contact" className="block font-serif text-xl py-4 text-neutral-950 hover:text-wine-700 transition-colors" onClick={() => setMobileMenuOpen(false)}>Contact</Link>
            </nav>
          </div>
        </div>
      )}

      {/* Search Overlay */}
      {searchOpen && (
        <Suspense fallback={null}>
          <SearchOverlay isOpen={searchOpen} onClose={closeSearch} />
        </Suspense>
      )}
    </>
  );
};

export default Navigation;
